import { Injectable } from "@angular/core";
import { catchError, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { TrafficController, AdminTrafficControllerCreationRequestDTO, TrafficControllerRequest } from "./traffic-controller.interface";

@Injectable()
export class TrafficControllerService {

    baseUrl = environment.administration_url + '/admin/traffic-controllers';
    appUrl = environment.administration_url + '/app/traffic-controllers';
    private _trafficController: ReplaySubject<TrafficController> = new ReplaySubject<TrafficController>(1);
    private _trafficControllers: ReplaySubject<TrafficController[]> = new ReplaySubject<TrafficController[]>(1);
    private _metadata: ReplaySubject<any> = new ReplaySubject<any>(1);

    set trafficController(value: TrafficController) {
        this._trafficController.next(value);
    }

    get trafficController$() {
        return this._trafficController.asObservable();
    }

    set trafficControllers(value: TrafficController[]) {
        this._trafficControllers.next(value);
    }

    get trafficControllers$() {
        return this._trafficControllers.asObservable();
    }

    set metadata(value: any) {
        this._metadata.next(value);
    }

    get metadata$() {
        return this._metadata.asObservable();
    }

    constructor(
        private _httpClient: HttpClient
    ) { }

    getAll(filters?: { page?: number; size?: number; keyword?: string }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.page !== undefined) params = params.set('page', filters.page.toString());
            if (filters.size !== undefined) params = params.set('size', filters.size.toString());
            if (filters.keyword) params = params.set('keyword', filters.keyword);
        }
        return this._httpClient.get<any>(`${this.baseUrl}`, { params })
        .pipe(
            tap((response: any) => {
                this.trafficControllers = response?.content || [];
                this.metadata = response;
                return response;
            }),
            catchError(() => of({ content: [] }))
        );
    }

    getById(uuid: string): Observable<TrafficController> {
        return this._httpClient.get<TrafficController>(`${this.appUrl}/${uuid}`)
        .pipe(
            tap((controller) => {
                this.trafficController = controller;
                return controller;
            }),
            catchError(() => of({} as TrafficController))
        );
    }

    createWithUser(dto: AdminTrafficControllerCreationRequestDTO): Observable<TrafficController> {
        return this._httpClient.post<TrafficController>(`${this.baseUrl}-and-users`, dto)
        .pipe(
            tap((controller) => {
                this.trafficController = controller;
                return controller;
            })
        );
    }

    update(uuid: string, data: TrafficControllerRequest): Observable<TrafficController> {
        return this._httpClient.put<TrafficController>(`${this.appUrl}/${uuid}`, data)
        .pipe(
            tap((controller) => {
                this.trafficController = controller;
                return controller;
            })
        );
    }

    activate(uuid: string): Observable<TrafficController> {
        return this._httpClient.patch<TrafficController>(`${this.appUrl}/${uuid}/activate`, {})
        .pipe(
            tap((controller) => {
                this.trafficController = controller;
                return controller;
            }),
            catchError(() => of({} as TrafficController))
        );
    }

    deactivate(uuid: string): Observable<TrafficController> {
        return this._httpClient.patch<TrafficController>(`${this.appUrl}/${uuid}/deactivate`, {})
        .pipe(
            tap((controller) => {
                this.trafficController = controller;
                return controller;
            }),
            catchError(() => of({} as TrafficController))
        );
    }

    assignChiefs(uuid: string, chiefUuids: string[]): Observable<TrafficController> {
        return this._httpClient.patch<TrafficController>(`${this.appUrl}/${uuid}/assign-chiefs`, { chiefUuids })
        .pipe(
            tap((controller) => {
                this.trafficController = controller;
                return controller;
            }),
            catchError(() => of({} as TrafficController))
        );
    }
}
